import React, { useState } from "react";
import Card from "../common/Card";
import Icon from "../common/Icon";
import Tooltip from "./Tooltip";
import { formatNumber, formatPercentage } from "../../utils/formatters";
import { METRIC_TOOLTIPS } from "../../utils/constants";
import { exportToCSV } from "../../utils/exportUtils";
import "./CompetitorsTable.css";

const CompetitorsTable = ({ competitors, clientDomain }) => {
  const [sortConfig, setSortConfig] = useState({
    key: "shareOfVoice",
    direction: "desc",
  });

  if (!competitors || competitors.length === 0) {
    return (
      <Card className="competitors-table-card">
        <h3 className="table-title">Top domaines concurrents</h3>
        <p className="table-empty">Aucune donnée disponible</p>
      </Card>
    );
  }

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCompetitors = [...competitors].sort((a, b) => {
    if (sortConfig.key === "domain") {
      return sortConfig.direction === "asc"
        ? a.domain.localeCompare(b.domain)
        : b.domain.localeCompare(a.domain);
    }

    const aValue = a[sortConfig.key] || 0;
    const bValue = b[sortConfig.key] || 0;

    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "ChevronsUpDown";
    return sortConfig.direction === "asc" ? "ChevronUp" : "ChevronDown";
  };

  const handleExportCSV = () => {
    const exportData = sortedCompetitors.map((competitor, index) => {
      const rank1To3 = competitor.rank1To3Count || 0;
      const rank4To10 = (competitor.rank4To5Count || 0) + (competitor.rank6To10Count || 0);
      const rank11To20 = competitor.rank11To20Count || 0;
      const rank21Plus = competitor.rank21PlusCount || 0;
      const top10 = rank1To3 + rank4To10;

      return {
        '#': index + 1,
        'Domaine': competitor.domain,
        'Part de voix (%)': competitor.shareOfVoice?.toFixed(2) || 0,
        'Trafic estimé': competitor.estimatedTraffic || 0,
        'Taux de présence (%)': competitor.presenceRate?.toFixed(0) || 0,
        'Top 10': top10,
        '1-3': rank1To3,
        '4-10': rank4To10,
        '11-20': rank11To20,
        '21+': rank21Plus
      };
    });

    exportToCSV(exportData, `top-domaines-${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <Card className="competitors-table-card">
      <div className="table-header-with-actions">
        <h3 className="table-title">Top domaines concurrents</h3>
        <button onClick={handleExportCSV} className="export-button">
          <Icon name="Download" size={16} />
          Exporter CSV
        </button>
      </div>

      <div className="table-container">
        <table className="competitors-table">
          <thead>
            <tr>
              <th>
                <div className="th-content">#</div>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.DOMAIN}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("domain")}
                  >
                    Domaine
                    <Icon name={getSortIcon("domain")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.SHARE_OF_VOICE}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("shareOfVoice")}
                  >
                    Part de voix
                    <Icon name={getSortIcon("shareOfVoice")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.ESTIMATED_TRAFFIC}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("estimatedTraffic")}
                  >
                    Trafic estimé
                    <Icon name={getSortIcon("estimatedTraffic")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.PRESENCE_RATE}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("presenceRate")}
                  >
                    Taux de présence
                    <Icon name={getSortIcon("presenceRate")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.TOP_10}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("rank1To3Count")}
                  >
                    Top 10
                    <Icon name={getSortIcon("rank1To3Count")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.POSITION_1_3}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("rank1To3Count")}
                  >
                    1-3
                    <Icon name={getSortIcon("rank1To3Count")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.POSITION_4_10}>
                  <div
                    className="th-content"
                    onClick={() => handleSort("rank6To10Count")}
                  >
                    4-10
                    <Icon name={getSortIcon("rank6To10Count")} size={16} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.POSITION_11_20}>
                  <div className="th-content">11-20</div>
                </Tooltip>
              </th>
              <th>
                <Tooltip text={METRIC_TOOLTIPS.POSITION_21_PLUS}>
                  <div className="th-content">21+</div>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCompetitors.map((competitor, index) => {
              const isClient = competitor.domain === clientDomain;
              const rank1To3 = competitor.rank1To3Count || 0;
              const rank4To10 = (competitor.rank4To5Count || 0) + (competitor.rank6To10Count || 0);
              const rank11To20 = competitor.rank11To20Count || 0;
              const rank21Plus = competitor.rank21PlusCount || 0;
              const top10 = rank1To3 + rank4To10;

              return (
                <tr
                  key={competitor.domain}
                  className={isClient ? "client-row" : ""}
                >
                  <td className="index-cell">{index + 1}</td>
                  <td className="domain-cell">
                    {competitor.domain}
                    {isClient && <span className="client-badge">Vous</span>}
                  </td>
                  <td className="metric-cell">
                    {formatPercentage(competitor.shareOfVoice, 1)}
                  </td>
                  <td className="metric-cell">
                    {formatNumber(competitor.estimatedTraffic)}
                  </td>
                  <td className="metric-cell">
                    {formatPercentage(competitor.presenceRate, 0)}
                  </td>
                  <td className="metric-cell metric-highlight">
                    {top10}
                  </td>
                  <td className="position-cell position-top3">
                    {rank1To3}
                  </td>
                  <td className="position-cell position-top10">{rank4To10}</td>
                  <td className="position-cell position-top20">{rank11To20}</td>
                  <td className="position-cell position-below">{rank21Plus}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default CompetitorsTable;
